import { useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { copy } from '~/eds/src/utils/copy';
import { EntityType } from '~/enums/EntityType';
import { PromptModelConfig } from '~/redux/api/types/sylvanus/model';
import { Nullable } from '~/types/Nullable';

import { ModelProgress } from '../types/model';
import { ConfigurationDetails } from './ConfigurationDetails';
import { PublishProgressMetrics } from './PublishProgressMetrics';

interface Props {
  config: Nullable<PromptModelConfig>;
  entityType: Nullable<EntityType.DocumentAttachedData | EntityType.Field>;
  isLoading: boolean;
  modelJson: string;
  progress: Nullable<ModelProgress>;
  datePublished: Nullable<string>;
}

export const DebugModelOverview = ({
  config,
  entityType,
  isLoading,
  modelJson,
  progress,
  datePublished,
}: Props) => {
  const intl = useIntl();
  const { toast } = useToast();
  const copyJsonAction = {
    icon: 'clipboard' as const,
    text: intl.formatMessage({
      id: 'CustomAiDebugModel.copyModelPublishJson',
      defaultMessage: 'Copy Publish JSON',
      description:
        'Label for the button to copy the Model Publish JSON data to the clipboard',
    }),
    onClick: () => {
      copy(modelJson);
      toast({
        message: intl.formatMessage({
          id: 'CustomAiDebugModel.copyConfigIdToast2',
          defaultMessage: 'Copied',
          description:
            'Success message that the text was copied to the clipboard',
        }),
        status: 'success',
      });
    },
  };
  return (
    <ContentContainer loadingContent={{ isLoading }}>
      <>
        {config && entityType && (
          <ConfigurationDetails
            config={config}
            entityType={entityType}
            maxW={progress ? `calc(100% - 504px)` : undefined}
          />
        )}
        {progress && (
          <Panel
            footer={{ actions: [copyJsonAction] }}
            offsetTop={118}
            position="right"
            title={intl.formatMessage({
              id: 'CustomAiDebugModel.modelPublishDetailsLabel',
              defaultMessage: 'Model Publish Details',
              description: 'Label text for the model publish details',
            })}
            width="panel.width.m"
          >
            <PublishProgressMetrics
              progress={progress}
              datePublished={datePublished}
            />
          </Panel>
        )}
      </>
    </ContentContainer>
  );
};
